import React, { useState } from "react";
import "./OrderDetails.css";
import { Download, ChevronDown, ChevronUp, ArrowLeft } from "lucide-react";
import { useOrderDetails } from "../../customHooks/Orders/useOrderDetails";
import {
  formatCurrency,
  getStatusIcon,
  mapStatusToBootstrap,
} from "../../components/Constants/Constants";
import AppLoader from "../../components/AppLoader/appLoader";

// 2. Updated IssueTable component (to be placed before the main function)

// Helper component to render the Issue Table content
const IssueTable = ({ issues, isCounterParty = false }) => (
    // Use mt-2 class to ensure table starts below the heading
    <table className="table table-sm table-striped small mt-2">
        <thead>
            <tr>
                {/* Apply issue-id-col for long text management */}
                <th className="issue-id-col text-start">Issue ID</th> 
                <th className="text-start">Category</th>
                <th className="issue-source-col text-start">Source</th>
                <th className="issue-status-col text-center">Status</th> {/* Center STATUS */}
                <th className="issue-date-col text-start">Created At</th>
            </tr>
        </thead>
        <tbody>
            {issues.map((issue, index) => (
                <tr key={index}>
                    <td className="font-monospace issue-id-col">{issue.issue_id}</td>
                    <td className="text-start">{issue.issue_category}</td>
                    <td className="issue-source-col text-start">
                        {isCounterParty ? issue.complainant_name : "You"}
                    </td>
                    <td className="issue-status-col">
                        {/* Use d-inline-block to ensure the badge centers properly */}
                        <span className={`badge ${issue.issue_status === 'PROCESSING' ? 'bg-warning text-dark' : 'bg-secondary'} d-inline-block`}>
                            {issue.issue_status}
                        </span>
                    </td>
                    <td className="issue-date-col text-start">{issue.issue_created_at}</td>
                </tr>
            ))}
        </tbody>
    </table>
);

// ... Rest of OrderDetails component remains the same ...

function OrderDetails({ orderId, hideHeaderSection }) {
  const {
    orderData,
    loading,
    error,
    calculateDaysRemaining,
    isReturnExpired,
    handleGetInvoice,
    handleGoBack,
    getProviderDetails, // Get the safe access function
  } = useOrderDetails({ orderId });

  const providerDetails = getProviderDetails(); // Fetch provider details once

  const [expanded, setExpanded] = useState({
    overview: true,
    buyer: true,
    seller: false,
    provider: false, // Added initial state for provider
  });

  const toggleSection = (section) => {
    setExpanded((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Safely access nested data
  const orderDetails = orderData?.order_details;
  const orderSummary = orderData?.order_summary;
  const buyerDetails = orderData?.buyer_details;
  const sellerDetails = orderData?.seller_details;
  const issueData = orderData?.IssueListingData;

  const totalIssuesCount =
    (issueData?.ReportedIssues?.length || 0) +
    (issueData?.CounterPartyIssues?.length || 0);

  if (loading) {
    return <AppLoader />;
  }

  // Handle error state
  if (error) {
    return <div className="alert alert-danger">Error: {error}</div>;
  }

  // Handle no data case after loading
  if (!orderData) {
    return <div className="alert alert-info">No order details found.</div>;
  }

  return (
    // <>
    //   {/* {canViewOrdersDetail ? ( */}
    //   ... (unauthorized check commented out)
    // </>
    <div>
      <div className="min-vh-100 bg-light">
        {/* Header */}
        {!hideHeaderSection && (
          <div className="mb-2">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <button
                    className="btn btn-link text-primary p-0 d-flex align-items-center text-decoration-none me-3"
                    onClick={handleGoBack}
                    style={{ fontSize: "16px", fontWeight: "500" }}
                    aria-label="Go back to orders list"
                  >
                    <ArrowLeft size={20} style={{ marginRight: "5px" }} />
                    Back
                  </button>
                  <div>
                    <p
                      className="mb-0 small"
                      style={{
                        fontSize: "16px",
                        marginLeft: "5px",
                        fontWeight: "500",
                      }}
                    >
                      Order ID:{" "}
                      <span
                        style={{
                          fontSize: "16px",
                          fontWeight: "500",
                          marginLeft: "5px",
                        }}
                      >
                        {orderDetails?.order_id}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="row g-4">
          {/* Left Panel - Order Items */}
          <div className="col-lg-8">
            <div className="card">
              <div className="card-header bg-white d-flex justify-content-between align-items-center">
                <span
                  style={{ fontSize: "18px", fontWeight: "600" }}
                  className="mb-0"
                >
                  Order Items
                </span>
                <button
                  className="btn btn-link text-primary p-0 d-flex align-items-center text-decoration-none"
                  onClick={handleGetInvoice}
                  disabled={!orderDetails?.invoice_url} // Disable if no invoice URL
                >
                  <Download size={16} className="me-1" />
                  Download Invoice
                </button>
              </div>

              {/* Order Items List */}
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-12 order-items-scroll">
                    {orderSummary?.products?.map((item, index) => (
                      <div key={index} className="mb-3 border-bottom pb-3">
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="d-flex align-items-center">
                            {/* Assuming item_image_urls[0] holds the main image */}
                            {item.item_image_urls?.[0] && (
                              <img
                                src={item.item_image_urls[0]}
                                alt={item.item_name}
                                style={{
                                  width: "50px",
                                  height: "50px",
                                  objectFit: "cover",
                                  marginRight: "15px",
                                  borderRadius: "4px",
                                }}
                              />
                            )}
                            <div>
                              <h6 className="mb-1">{item.item_name}</h6>
                              <div className="d-flex gap-4">
                                <small
                                  style={{ fontSize: "12px" }}
                                  className="text-muted"
                                >
                                  Unit Price: {formatCurrency(item.unit_price)}
                                </small>
                                <small
                                  style={{ fontSize: "12px" }}
                                  className="text-muted"
                                >
                                  Quantity: {item.quantity}
                                </small>
                              </div>
                            </div>
                          </div>
                          <div className="text-end">
                            <div className="fw-bold">
                              {formatCurrency(item.quantity * item.unit_price)}
                            </div>

                            {/* GST on Product */}
                            {item.product_gst_percentage !== 0 &&
                              !item.is_product_gst_inclusive && (
                                <>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      fontSize: "12px",
                                      gap: "50px",
                                    }}
                                  >
                                    <span className="text-muted">
                                      {item.product_gst_percentage}% GST
                                    </span>
                                    <span>
                                      {formatCurrency(
                                        item.calc_product_gst_amount
                                      )}
                                    </span>
                                  </div>
                                </>
                              )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Financial Summary */}
                <div>
                  <div className="row g-2">
                    {/* Sub Total (Product Prices + Product GST) */}
                    <div
                      className="col-12 d-flex justify-content-between"
                      style={{ fontSize: "14px" }}
                    >
                      <span>Sub Total (Items + GST)</span>
                      <span>
                        {formatCurrency(
                          orderSummary?.sub_total_amount_with_gst
                        )}
                      </span>
                    </div>

                    {/* Convenience Fee */}
                    {orderSummary?.is_convenience_fee_applied &&
                      (orderSummary?.convenience_fee > 0 ||
                        orderSummary?.gst_convenience_fee > 0) && (
                        <div
                          className="col-12 d-flex justify-content-between"
                          style={{ fontSize: "14px" }}
                        >
                          <span>Convenience Fee</span>
                          <div className="text-end">
                            <div>
                              {formatCurrency(orderSummary?.convenience_fee)}
                            </div>
                            {/* GST on Convenience Fee */}
                            {orderSummary?.convenience_gst_percentage > 0 &&
                              orderSummary?.gst_convenience_fee > 0 && (
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    fontSize: "12px",
                                    gap: "50px",
                                  }}
                                >
                                  <span className="text-muted">
                                    {orderSummary?.convenience_gst_percentage}%
                                    GST
                                  </span>
                                  <span>
                                    {formatCurrency(
                                      orderSummary?.gst_convenience_fee
                                    )}
                                  </span>
                                </div>
                              )}
                          </div>
                        </div>
                      )}

                    {/* Delivery Charges (Show if amount > 0 or GST > 0) */}
                    {(orderSummary?.delivery_charges > 0 ||
                      orderSummary?.gst_on_delivery_charges > 0) && (
                      <div
                        className="col-12 d-flex justify-content-between"
                        style={{ fontSize: "14px" }}
                      >
                        <span>Delivery Charges</span>
                        <div className="text-end">
                          <div>
                            {formatCurrency(orderSummary?.delivery_charges)}
                          </div>
                          {/* GST on Delivery Charges */}
                          {orderSummary?.delivery_charges_gst_percentage > 0 &&
                            orderSummary?.gst_on_delivery_charges > 0 && (
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  fontSize: "12px",
                                  gap: "50px",
                                }}
                              >
                                <span className="text-muted">
                                  {
                                    orderSummary?.delivery_charges_gst_percentage
                                  }
                                  % GST
                                </span>
                                <span>
                                  {formatCurrency(
                                    orderSummary?.gst_on_delivery_charges
                                  )}
                                </span>
                              </div>
                            )}
                        </div>
                      </div>
                    )}

                    {/* Buyer Finder Fee Details (Show if total fee > 0) */}
                    {orderSummary?.total_buyer_finder_fee > 0 && (
                      <div className="mt-2 pt-3 border-top w-100">
                        {/* Buyer Fee On Order Amount (Show if amount > 0) */}
                        {orderSummary?.buyer_finder_fee_on_order_amount > 0 && (
                          <div
                            className="col-12 d-flex justify-content-between"
                            style={{ fontSize: "14px" }}
                          >
                            <span>Buyer Fee On Order Value</span>
                            <div className="text-end">
                              <div>
                                {formatCurrency(
                                  orderSummary?.buyer_finder_fee_on_order_amount
                                )}
                              </div>
                              {/* Display fee percentage if available, adjusted logic for amount type fee */}
                              {orderSummary?.buyer_finder_fee_type ===
                                "amount" && (
                                <span className="text-muted">
                                  {formatCurrency(
                                    orderSummary?.buyer_finder_fee
                                  )}{" "}
                                  BFF Fee Amount
                                </span>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Buyer Fee On Delivery Charges (Show if amount > 0) */}
                        {orderSummary?.buyer_finder_fee_on_delivery_charges >
                          0 && (
                          <div
                            className="col-12 d-flex justify-content-between"
                            style={{ fontSize: "14px" }}
                          >
                            <span>Buyer Fee On Delivery Charges</span>
                            <span>
                              {formatCurrency(
                                orderSummary?.buyer_finder_fee_on_delivery_charges
                              )}
                            </span>
                          </div>
                        )}

                        {/* Total Buyer Finder Fee */}
                        <div className="col-12 d-flex justify-content-between fw-bold text-danger mt-2">
                          <span>Total Buyer Finder Fee (Incl. GST)</span>
                          <span>
                            {formatCurrency(
                              orderSummary?.total_buyer_finder_fee
                            )}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Total */}
                    <div className="col-12 border-top pt-2">
                      <div className="d-flex justify-content-between fw-bold fs-5">
                        <span>Total Payable</span>
                        <span>
                          {formatCurrency(orderSummary?.total_order_amount)}
                        </span>
                      </div>
                    </div>

                    {/* Amount to be Settle (Note: Field is missing in provided JSON structure under order_summary, accessing if it might exist) */}
                    <div className="col-12 d-flex justify-content-between fw-bold text-success">
                      <span>Amount To Be Settle (to Seller)</span>
                      <span>
                        {/* Assuming this is what the provider should receive for the product */}
                        {formatCurrency(providerDetails?.amount)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>


{issueData && totalIssuesCount > 0 && (
    // Use a container div with margin bottom for spacing
    <div className="mt-4"> 
        {/* Header/Toggle Section - styled to look like a header but without card context */}
        <div
            className="p-3 bg-white border d-flex justify-content-between align-items-center"
            onClick={() => toggleSection("issues")}
            style={{ cursor: "pointer", borderRadius: '0.25rem 0.25rem 0 0' }} // Keep top rounded corners
        >
            <span
                style={{ fontSize: "18px", fontWeight: "600" }}
                className="mb-0"
            >
                Reported Issues ({totalIssuesCount})
            </span>
            {expanded.issues ? (
                <ChevronUp size={16} />
            ) : (
                <ChevronDown size={16} />
            )}
        </div>
        
        {/* Collapsible Content Area */}
        {expanded.issues && (
            <div className="p-3 border border-top-0" style={{ backgroundColor: '#f8f9fa', borderRadius: '0 0 0.25rem 0.25rem' }}>
                {/* Render Reported Issues (Self) */}
                {issueData.ReportedIssues?.length > 0 && (
                    <>
                        <h6 className="mt-2 mb-2">
                            Issues Reported by You ({issueData.ReportedIssues.length})
                        </h6>
                        <IssueTable
                            issues={issueData.ReportedIssues}
                            isCounterParty={false}
                        />
                    </>
                )}

                {/* Render Counter-Party Issues (Others) */}
                {issueData.CounterPartyIssues?.length > 0 && (
                    <>
                        <h6 className={`mt-3 mb-2 ${issueData.ReportedIssues?.length > 0 ? "pt-2 border-top" : ""}`}>
                            Issues Reported by Counter-Party ({issueData.CounterPartyIssues.length})
                        </h6>
                        <IssueTable
                            issues={issueData.CounterPartyIssues}
                            isCounterParty={true}
                        />
                    </>
                )}
            </div>
        )}
    </div>
)}

          </div>

          {/* Right Panel */}
          <div className="col-lg-4">
            <div className="row g-4">
              {/* Order Overview */}
              <div className="col-12">
                <div className="card">
                  <div
                    className="card-header bg-white d-flex justify-content-between align-items-center"
                    onClick={() => toggleSection("overview")}
                    style={{ cursor: "pointer" }}
                  >
                    <span
                      style={{ fontSize: "18px", fontWeight: "600" }}
                      className="mb-0"
                    >
                      Order Overview
                    </span>
                    {expanded.overview ? (
                      <ChevronUp size={16} />
                    ) : (
                      <ChevronDown size={16} />
                    )}
                  </div>
                  {expanded.overview && (
                    <div className="card-body">
                      <div className="row g-2">
                        <div className="col-12 d-flex justify-content-between align-items-center">
                          <span className="small">Order Status</span>
                          <span
                            className={mapStatusToBootstrap(
                              orderDetails?.order_status
                            )}
                          >
                            {getStatusIcon(orderDetails?.order_status)}
                            &nbsp;
                            {orderDetails?.order_status}{" "}
                          </span>
                        </div>
                        <div className="col-12 d-flex justify-content-between align-items-center">
                          <span className="small">Order Category</span>
                          <span className="small">
                            {orderDetails?.order_category}
                          </span>
                        </div>
                        <div className="col-12 d-flex justify-content-between align-items-center">
                          <span className="small">Transaction Id</span>
                          <span className="small font-monospace">
                            {buyerDetails?.order_transaction_id}
                          </span>
                        </div>
                        <div className="col-12 d-flex justify-content-between align-items-center">
                          <span className="small">Return Window</span>
                          <span className="small">
                            {orderDetails?.return_window} days
                          </span>
                        </div>
                        <div className="col-12 d-flex justify-content-between align-items-center">
                          <span className="small">Return Expiry</span>
                          <div className="text-end">
                            <div className="small">
                              {orderDetails?.return_expires_at}
                            </div>
                            {isReturnExpired() && (
                              <small className="text-danger fw-bold">
                                Expired
                              </small>
                            )}
                          </div>
                        </div>
                        <div className="col-12 d-flex justify-content-between align-items-center">
                          <span className="small">Order Created At</span>
                          <span className="small">
                            {orderDetails?.order_created_at}
                          </span>
                        </div>
                        <div className="col-12 d-flex justify-content-between align-items-center">
                          <span className="small">Order Delivered At</span>
                          <span className="small">
                            {orderDetails?.order_delivered_at}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Buyer Details */}
              <div className="col-12">
                <div className="card">
                  <div
                    className="card-header bg-white d-flex justify-content-between align-items-center"
                    onClick={() => toggleSection("buyer")}
                    style={{ cursor: "pointer" }}
                  >
                    <span
                      style={{ fontSize: "18px", fontWeight: "600" }}
                      className="mb-0"
                    >
                      Buyer Details
                    </span>
                    {expanded.buyer ? (
                      <ChevronUp size={16} />
                    ) : (
                      <ChevronDown size={16} />
                    )}
                  </div>
                  {expanded.buyer && (
                    <div className="card-body">
                      <div className="row g-3">
                        <div className="col-12 d-flex justify-content-between align-items-center">
                          <span className="small">Buyer App ID</span>
                          <span className="small">
                            {buyerDetails?.buyer_app_id}
                          </span>
                        </div>
                        <div className="col-12 d-flex justify-content-between align-items-center">
                          <span className="small">Payment Status</span>
                          <span className="small">
                            {buyerDetails?.payment_status}
                          </span>
                        </div>
                        <div className="col-12 d-flex justify-content-between align-items-center">
                          <span className="small">Payment ID</span>
                          <span className="small">
                            {buyerDetails?.payment_id}
                          </span>
                        </div>
                        <div className="col-12 d-flex justify-content-between align-items-center">
                          <span className="small">Buyer App URI</span>
                          <span className="small">
                            <a
                              href={buyerDetails?.buyer_app_uri}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Link
                            </a>
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Seller Details */}
              <div className="col-12">
                <div className="card">
                  <div
                    className="card-header bg-white d-flex justify-content-between align-items-center"
                    onClick={() => toggleSection("seller")}
                    style={{ cursor: "pointer" }}
                  >
                    <span
                      style={{ fontSize: "18px", fontWeight: "600" }}
                      className="mb-0"
                    >
                      Seller Details
                    </span>
                    {expanded.seller ? (
                      <ChevronUp size={16} />
                    ) : (
                      <ChevronDown size={16} />
                    )}
                  </div>
                  {expanded.seller && (
                    <div className="card-body">
                      <div className="row g-3">
                        <div className="col-12 d-flex justify-content-between align-items-center">
                          <span className="small">Seller Order Id</span>
                          <span className="small">
                            {sellerDetails?.seller_order_id}
                          </span>
                        </div>
                        <div className="col-12 d-flex justify-content-between align-items-center">
                          <span className="small">Seller App ID</span>
                          <span className="small">
                            {sellerDetails?.seller_app_id}
                          </span>
                        </div>
                        <div className="col-12 d-flex justify-content-between align-items-center">
                          <span className="small">Seller App URI</span>
                          <span className="small">
                            <a
                              href={sellerDetails?.seller_app_uri}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Link
                            </a>
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Provider Details (Renamed from Provider for clarity and matching JSON) */}
              <div className="col-12">
                <div className="card">
                  <div
                    className="card-header bg-white d-flex justify-content-between align-items-center"
                    onClick={() => toggleSection("provider")}
                    style={{ cursor: "pointer" }}
                  >
                    <span
                      style={{ fontSize: "18px", fontWeight: "600" }}
                      className="mb-0"
                    >
                      Provider Details
                    </span>
                    {expanded.provider ? (
                      <ChevronUp size={16} />
                    ) : (
                      <ChevronDown size={16} />
                    )}
                  </div>
                  {expanded.provider && (
                    <div className="card-body">
                      <div className="row g-3">
                        <div className="col-12 d-flex justify-content-between align-items-center">
                          <span className="small">Provider ID</span>
                          <span className="small">
                            {providerDetails?.provider_id}
                          </span>
                        </div>
                        <div className="col-12 d-flex justify-content-between align-items-center">
                          <span className="small">Provider Name</span>
                          <span className="small">
                            {providerDetails?.provider_name}
                          </span>
                        </div>
                        <div className="col-12 d-flex justify-content-between align-items-center">
                          <span className="small">Bank Account No</span>
                          <span className="small">
                            {providerDetails?.bank_details?.account_no}
                          </span>
                        </div>
                        <div className="col-12 d-flex justify-content-between align-items-center">
                          <span className="small">IFSC Code</span>
                          <span className="small">
                            {providerDetails?.bank_details?.ifsc_code}
                          </span>
                        </div>
                        <div className="col-12 d-flex justify-content-between align-items-center">
                          <span className="small">Amount to Settle</span>
                          <span className="small">
                            {formatCurrency(providerDetails?.amount)}
                          </span>
                        </div>
                        <div className="col-12 d-flex justify-content-between align-items-center">
                          <span className="small">
                            Provider Settlement Status
                          </span>
                          <span className="small">
                            {providerDetails?.provider_settlement_status}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OrderDetails;
